// ─────────────────────────────────────────────
// KLOTHIQ — PDF Design System Helpers
// ─────────────────────────────────────────────

import jsPDF from 'jspdf';
import {
  COLOR,
  CARD_ACCENTS,
  PAGE,
  FONT,
  SPACE,
  BRAND_NAME,
  BRAND_TAGLINE,
  BRAND_PHONE,
  BRAND_ADDRESS,
  LOGO_BASE64,
  LOGO_WIDTH,
  LOGO_HEIGHT,
} from './pdfConstants';

/**
 * Returns ₹ formatted in Indian number system (e.g. 1500 → "₹1,500.00", 123456 → "₹1,23,456.00").
 */
export function formatCurrency(value: number): string {
  if (isNaN(value) || value === null || value === undefined) return 'Rs. 0.00';
  const isNegative = value < 0;
  const absVal = Math.abs(value);
  const [int = '0', dec = '00'] = absVal.toFixed(2).split('.');
  const lastThree = int.slice(-3);
  const rest = int.slice(0, -3);
  const formatted = rest ? rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + lastThree : lastThree;
  return `${isNegative ? '-' : ''}Rs. ${formatted}.${dec}`;
}

/**
 * Returns "15 Jan 2024" format. Null/undefined → "—".
 */
export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return '—';
  try {
    const d = typeof value === 'string' ? new Date(value) : value;
    if (isNaN(d.getTime())) return '—';
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const day = String(d.getDate()).padStart(2, '0');
    const month = months[d.getMonth()];
    const year = d.getFullYear();
    return `${day} ${month} ${year}`;
  } catch {
    return '—';
  }
}

/**
 * Capitalises and replaces underscores: "payment_pending" → "Payment Pending".
 */
export function formatStatus(status: string | null | undefined): string {
  if (!status) return '—';
  return status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Fallback logo renderer when LOGO_BASE64 is empty or invalid.
 * Renders a compact near-black badge with a gold accent bar and a
 * white "K" mark — matching the premium brand mark used across reports.
 */
function renderLogoFallback(doc: jsPDF): void {
  doc.setFillColor(...COLOR.ink);
  doc.roundedRect(PAGE.marginL, PAGE.marginT, LOGO_WIDTH, LOGO_HEIGHT, 1.5, 1.5, 'F');

  // Gold accent bar inset on the left edge of the badge
  doc.setFillColor(...COLOR.gold);
  doc.rect(PAGE.marginL + 1.4, PAGE.marginT + 2, 1.1, LOGO_HEIGHT - 4, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(...COLOR.white);
  doc.text('K', PAGE.marginL + LOGO_WIDTH / 2 + 1, PAGE.marginT + LOGO_HEIGHT / 2 + 3, {
    align: 'center',
  });
}

/**
 * Draws the standard premium page header:
 * [logo + brand block] · [report title, accent-blue] · [address/phone, right]
 * with a gold rule beneath. Returns the Y coordinate after the header.
 */
export function drawHeader(
  doc: jsPDF,
  reportTitle: string,
  subtitle?: string,
  customerFacing = false,
): number {
  // Logo or Fallback badge
  if (LOGO_BASE64 && LOGO_BASE64.trim() !== '') {
    try {
      const format = LOGO_BASE64.startsWith('data:image/jpeg') ? 'JPEG' : 'PNG';
      doc.addImage(LOGO_BASE64, format, PAGE.marginL, PAGE.marginT, LOGO_WIDTH, LOGO_HEIGHT);
    } catch {
      renderLogoFallback(doc);
    }
  } else {
    renderLogoFallback(doc);
  }

  const brandX = PAGE.marginL + LOGO_WIDTH + 4;

  // Brand Name
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(FONT.brandName);
  doc.setTextColor(...COLOR.textDark);
  doc.text(BRAND_NAME.toUpperCase(), brandX, PAGE.marginT + 6);

  // Caption beneath brand name
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(FONT.tagline);
  doc.setTextColor(...COLOR.textMuted);
  doc.text(
    `${BRAND_TAGLINE}${customerFacing ? '' : ' · ADMIN'}`,
    brandX,
    PAGE.marginT + 10.5,
  );

  // Report Title — centered accent-blue block
  const titleX = brandX + 44;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(FONT.reportTitle);
  doc.setTextColor(...COLOR.blue);
  doc.text(reportTitle, titleX, PAGE.marginT + 8);

  // Right column — brand address / phone (static contact block)
  const rightX = PAGE.width - PAGE.marginR;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(FONT.small);
  doc.setTextColor(...COLOR.textMuted);
  doc.text(BRAND_ADDRESS.join(', '), rightX, PAGE.marginT + 5, { align: 'right' });
  doc.text(`IN · ${BRAND_PHONE}`, rightX, PAGE.marginT + 9, { align: 'right' });

  // Subtitle / export context (beneath report title)
  if (subtitle) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(FONT.small);
    doc.setTextColor(...COLOR.textMuted);
    doc.text(subtitle, titleX, PAGE.marginT + 13);
  }

  // Gold rule beneath header block
  const ruleY = PAGE.marginT + LOGO_WIDTH + 2;
  doc.setDrawColor(...COLOR.gold);
  doc.setLineWidth(0.6);
  doc.line(PAGE.marginL, ruleY, PAGE.width - PAGE.marginR, ruleY);

  return PAGE.marginT + SPACE.headerH;
}

/**
 * Draws footer on the active page.
 */
export function drawFooter(
  doc: jsPDF,
  pageNumber: number,
  totalPages: number,
  customerFacing = false,
  label?: string,
): void {
  const yRule = PAGE.height - PAGE.marginB - 6;
  const yText = PAGE.height - PAGE.marginB - 2;

  // Gold rule
  doc.setDrawColor(...COLOR.gold);
  doc.setLineWidth(0.4);
  doc.line(PAGE.marginL, yRule, PAGE.width - PAGE.marginR, yRule);

  // Left text
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(FONT.footer);
  doc.setTextColor(...COLOR.textMuted);
  doc.text(
    label ?? (customerFacing ? 'Klothiq - Order receipt' : 'Klothiq — Confidential'),
    PAGE.marginL,
    yText,
  );

  // Center text
  const todayStr = formatDate(new Date());
  doc.text(`Generated ${todayStr}`, PAGE.width / 2, yText, { align: 'center' });

  // Right text
  doc.text(`Page ${pageNumber} of ${totalPages}`, PAGE.width - PAGE.marginR, yText, {
    align: 'right',
  });
}

/**
 * Draws a full-width ink section bar with a gold left accent stripe,
 * white bold title, and an optional right-aligned muted caption.
 * Returns the Y coordinate after the section bar.
 */
export function drawSectionTitle(doc: jsPDF, title: string, y: number, rightText?: string): number {
  const barH = 8;

  doc.setFillColor(...COLOR.ink);
  doc.roundedRect(PAGE.marginL, y, PAGE.contentW, barH, 1, 1, 'F');

  // Gold accent stripe
  doc.setFillColor(...COLOR.gold);
  doc.rect(PAGE.marginL, y + 1, 1.6, barH - 2, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(FONT.sectionTitle);
  doc.setTextColor(...COLOR.white);
  doc.text(title.toUpperCase(), PAGE.marginL + 6, y + barH / 2 + 1.6);

  if (rightText) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(FONT.small);
    doc.setTextColor(...COLOR.gold);
    doc.text(rightText, PAGE.width - PAGE.marginR - 4, y + barH / 2 + 1.6, { align: 'right' });
  }

  return y + barH + 5;
}

// ── KPI Stat Cards ───────────────────────────────────────────────────────────

export interface StatCard {
  value: string;
  label: string;
  trend?: string;
  /** false renders the trend in danger red instead of success green */
  trendUp?: boolean;
  /** Overrides the auto-rotated accent color */
  accent?: [number, number, number];
}

/**
 * Draws a row of KPI summary cards (colored top border, big value, muted
 * label, optional trend line) — mirrors the report-cover stat tiles.
 * Returns the Y coordinate after the card row.
 */
export function drawStatCards(doc: jsPDF, cards: StatCard[], y: number): number {
  if (cards.length === 0) return y;

  const gap = 4;
  const n = cards.length;
  const cardW = (PAGE.contentW - gap * (n - 1)) / n;
  const cardH = SPACE.statCardH;
  const topBarH = 1.4;

  cards.forEach((card, i) => {
    const x = PAGE.marginL + i * (cardW + gap);
    const accent = card.accent ?? CARD_ACCENTS[i % CARD_ACCENTS.length]!;

    doc.setFillColor(...accent);
    doc.rect(x, y, cardW, topBarH, 'F');

    doc.setFillColor(...COLOR.rowAlt);
    doc.setDrawColor(...COLOR.border);
    doc.setLineWidth(0.2);
    doc.rect(x, y + topBarH, cardW, cardH - topBarH, 'FD');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(FONT.statValue);
    doc.setTextColor(...COLOR.textDark);
    doc.text(card.value, x + 4, y + 11);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(FONT.statLabel);
    doc.setTextColor(...COLOR.textMuted);
    doc.text(card.label, x + 4, y + 16.5);

    if (card.trend) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.5);
      doc.setTextColor(...(card.trendUp === false ? COLOR.danger : COLOR.success));
      doc.text(card.trend, x + 4, y + 20.5);
    }
  });

  return y + cardH + 6;
}

/**
 * Maps a status/state string to a semantic color (success/warning/danger/muted)
 * used for table status columns and badges.
 */
export function getStatusColor(status: string | null | undefined): [number, number, number] {
  const s = (status ?? '').toLowerCase();
  if (/(return|refund)/.test(s)) return COLOR.purple;
  if (/(paid|completed|in.?stock|active|delivered|success|available|ok)/.test(s))
    return COLOR.success;
  if (/(pending|processing|low.?stock|partial|due)/.test(s)) return COLOR.warning;
  if (/(out.?of.?stock|cancelled|canceled|overdue|failed|inactive|unpaid)/.test(s))
    return COLOR.danger;
  return COLOR.textMuted;
}

/**
 * Draws a label:value pair used in receipt detail sections.
 */
export function drawKeyValueRow(
  doc: jsPDF,
  label: string,
  value: string,
  x: number,
  y: number,
  _colWidth?: number,
  valueColor?: [number, number, number],
): void {
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(FONT.small);
  doc.setTextColor(...COLOR.textMuted);
  const labelWithColon = `${label}: `;
  doc.text(labelWithColon, x, y);

  const labelWidth = doc.getTextWidth(labelWithColon);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...(valueColor ?? COLOR.textBody));
  doc.text(value, x + labelWidth, y);
}

/**
 * Returns the shared autoTable style config used by all tables.
 */
export function getAutoTableStyles(): Record<string, unknown> {
  return {
    styles: {
      font: 'helvetica',
      fontSize: FONT.tableBody,
      cellPadding: { top: 3, right: 4, bottom: 3, left: 4 },
      lineColor: COLOR.border,
      lineWidth: 0.2,
      textColor: COLOR.textBody,
      overflow: 'linebreak',
      minCellHeight: SPACE.rowH,
    },
    headStyles: {
      fillColor: COLOR.ink,
      textColor: COLOR.white,
      fontStyle: 'bold',
      fontSize: FONT.tableHeader,
      cellPadding: { top: 4, right: 2, bottom: 4, left: 2 },
      lineWidth: 0,
    },
    alternateRowStyles: {
      fillColor: COLOR.rowAlt,
    },
    columnStyles: {},
    tableLineColor: COLOR.border,
    tableLineWidth: 0.2,
    showHead: 'everyPage',
    margin: { left: PAGE.marginL, right: PAGE.marginR },
  };
}

/**
 * Iterates through all pages in the document and calls drawFooter on each.
 */
export function addPageNumbers(doc: jsPDF, customerFacing = false, label?: string): void {
  const total =
    typeof doc.getNumberOfPages === 'function'
      ? doc.getNumberOfPages()
      : ((doc.internal as unknown as { getNumberOfPages?: () => number }).getNumberOfPages?.() ??
        doc.internal.pages.length - 1);
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    drawFooter(doc, i, total, customerFacing, label);
  }
}
